import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import { FEATURE_FLAGS, useIsFeatureEnabled } from "@/feature-flags"
import { useCreatorResolver } from "@/organization"
import { useUser } from "@clerk/nextjs"
import {
  Button,
  cn,
  TABLE_CARD_CLASS,
  TableControlsRow,
  type RowAction,
  type ToolbarItem,
} from "@coval/ui"
import { Eye, Link2, Pencil, Trash2 } from "lucide-react"

import { EmptyFilteredState, EmptyStateCTA } from "@/components/EmptyStateCTA"
import { CollectionFilter } from "@/components/filters/CollectionFilter"
import { NewResourceButton } from "@/components/ResourceActionButton"
import { BrowseTable as Table } from "@/components/tables/BrowseTable"
import { RowNameEditor } from "@/components/tables/RowNameEditor"
import { TableBrowseToolbar } from "@/components/tables/TableBrowseToolbar"
import { TableDisplayScope } from "@/components/tables/TableDisplayScope"
import { useCollectionFilters } from "@/hooks/useCollectionFilters"
import { useQueryParams } from "@/hooks/useQueryParams"
import { isSameResourceName } from "@/utils/validation/resourceName"

import {
  isGeneratedNotebooksRefusal,
  listNotebookCatalog,
  NOTEBOOK_BUILDER_PATH,
  renameGeneratedNotebook,
} from "../utils/generatedNotebooks"
import {
  deleteNotebook,
  listNotebooks,
  renameNotebook,
} from "../utils/notebooksApi"
import {
  buildChatNotebookRows,
  buildNotebookSeries,
  type NotebookCatalog,
  type NotebookRow,
} from "../utils/notebookSeries"
import type { NotebookSummary } from "../utils/notebookSnapshot"
import { reportErrorText } from "../utils/reportClient"
import { DeleteNotebookDialog } from "./DeleteNotebookDialog"
import { buildNotebookColumns } from "./NotebookListColumns"
import { NotebookSeriesDialog } from "./NotebookSeriesDialog"

interface NotebooksListProps {
  organizationSlug: string
  workspaceId: string
  onOpen: (notebookId: string) => void
  /** Absolute-path builder for a notebook, used by "Copy link". */
  hrefFor: (notebookId: string) => string
}

const EMPTY_CATALOG: NotebookCatalog = { notebooks: [] }

// Matches the cap RenameNotebookDialog applies when a notebook is renamed from its own page.
const NOTEBOOK_TITLE_MAX_LENGTH = 200

const notebookRowKey = (notebook: NotebookRow) =>
  `${notebook.kind}:${notebook.id}`

/** Saved chats and generated notebooks for the active workspace in the standard list table. */
export function NotebooksList({
  organizationSlug,
  workspaceId,
  onOpen,
  hrefFor,
}: NotebooksListProps) {
  const reportsEnabled = useIsFeatureEnabled(FEATURE_FLAGS.SOFIA_NOTEBOOKS)
  const router = useRouter()
  const queryParams = useQueryParams()
  const { user, isLoaded: userLoaded } = useUser()
  const [chats, setChats] = useState<NotebookSummary[] | null>(null)
  // Generated rows and their load error carry the scope they were read for,
  // so a workspace switch or a refused replacement read never shows another
  // workspace's notebooks. The error stays apart from the saved-chat error,
  // which a later chat load clears.
  const scope = `${organizationSlug}:${workspaceId}`
  const [generated, setGenerated] = useState<{
    scope: string
    catalog: NotebookCatalog
  } | null>(null)
  const [generatedError, setGeneratedError] = useState<{
    scope: string
    message: string
  } | null>(null)
  const [generatedAttempt, setGeneratedAttempt] = useState(0)
  const [catalogSettledScope, setCatalogSettledScope] = useState<string | null>(
    null,
  )
  const generatedRows =
    reportsEnabled && generated?.scope === scope
      ? generated.catalog
      : EMPTY_CATALOG
  const generatedErrorMessage =
    reportsEnabled && generatedError?.scope === scope
      ? generatedError.message
      : null
  const [error, setError] = useState<string | null>(null)
  // The open rename field and the delete dialog remember the scope they were opened in and close outside it, so a workspace switch can never send an old row's id to the new scope.
  const [renaming, setRenaming] = useState<{
    row: NotebookRow
    scope: string
  } | null>(null)
  const [deleting, setDeleting] = useState<{
    row: NotebookRow
    scope: string
  } | null>(null)
  const renamingRow = renaming?.scope === scope ? renaming.row : null
  // The scope on screen now, so a rename that settles after a workspace switch drops its result.
  const currentScopeRef = useRef(scope)
  useEffect(() => {
    currentScopeRef.current = scope
  }, [scope])
  // The latest rename sent for each row, so an older rename that settles late never overwrites a newer one.
  const latestRenameRef = useRef(new Map<string, number>())
  const deletingRow = deleting?.scope === scope ? deleting.row : null
  const [series, setSeries] = useState<{
    id: string
    scope: string
  } | null>(null)
  const [deletePending, setDeletePending] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { resolveCreator, creatorOptions: peopleOptions } = useCreatorResolver()

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const next = await listNotebooks(organizationSlug, workspaceId, signal)
        if (!signal?.aborted) {
          setChats(next)
          setError(null)
        }
      } catch (loadError: unknown) {
        if (signal?.aborted) {
          return
        }
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Could not load notebooks.",
        )
        setChats([])
      }
    },
    [organizationSlug, workspaceId],
  )

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  useEffect(() => {
    setSeries(null)
    setCatalogSettledScope(null)
    if (!reportsEnabled) {
      return
    }
    const controller = new AbortController()
    const requested = `${organizationSlug}:${workspaceId}`
    // A deployment without the catalog endpoint leaves saved chats available; access and transient failures remain visible.
    listNotebookCatalog(organizationSlug, workspaceId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) {
          setGenerated({ scope: requested, catalog: next })
          setSeries(null)
          setGeneratedError(null)
        }
      })
      .catch((failure: unknown) => {
        if (controller.signal.aborted) {
          return
        }
        if (isGeneratedNotebooksRefusal(failure)) {
          // A refusal now means rows read earlier are no longer available.
          setGenerated(null)
          setGeneratedError(null)
          return
        }
        setGeneratedError({
          scope: requested,
          message: `Generated notebooks did not load. ${reportErrorText(failure)}`,
        })
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setCatalogSettledScope(requested)
        }
      })
    return () => controller.abort()
  }, [generatedAttempt, organizationSlug, reportsEnabled, workspaceId])

  // Generated notebooks live in the saving user's own store, so they are theirs.
  // The two stores write timestamps with different UTC offsets; sort by instant.
  const currentUserEmail = user?.primaryEmailAddress?.emailAddress ?? null
  const scheduleLoading =
    reportsEnabled &&
    ((generated?.scope !== scope && catalogSettledScope !== scope) ||
      (generated?.scope === scope &&
        generated.catalog.generative_schedules !== undefined &&
        !userLoaded))
  const notebooks = useMemo<NotebookRow[] | null>(
    () =>
      chats &&
      [
        ...buildChatNotebookRows(
          chats,
          generatedRows,
          currentUserEmail,
          scheduleLoading,
        ),
        ...buildNotebookSeries(generatedRows, currentUserEmail),
      ].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)),
    [chats, currentUserEmail, generatedRows, scheduleLoading],
  )

  const generatedHref = useCallback(
    (id: string) =>
      queryParams.getHref(
        `${NOTEBOOK_BUILDER_PATH}?notebook=${encodeURIComponent(id)}`,
        organizationSlug,
        workspaceId,
      ),
    [organizationSlug, queryParams, workspaceId],
  )
  const open = useCallback(
    (notebook: NotebookRow) => {
      if (notebook.kind === "series") {
        setSeries({ id: notebook.id, scope })
      } else if (notebook.kind === "generated") {
        void router.push(generatedHref(notebook.id))
      } else {
        onOpen(notebook.id)
      }
    },
    [generatedHref, onOpen, router, scope],
  )

  const confirmDelete = useCallback(async () => {
    if (!deletingRow || deletePending) {
      return
    }
    setDeletePending(true)
    try {
      await deleteNotebook(organizationSlug, workspaceId, deletingRow.id)
      setChats((prev) =>
        prev ? prev.filter((item) => item.id !== deletingRow.id) : prev,
      )
      setDeleting(null)
    } catch (deleteError: unknown) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Could not delete the notebook.",
      )
    } finally {
      setDeletePending(false)
    }
  }, [deletePending, deletingRow, organizationSlug, workspaceId])

  // A failed rename rejects, so the row's title field stays open with the reason.
  const rename = useCallback(
    async (notebook: NotebookRow, title: string) => {
      const renameScope = `${organizationSlug}:${workspaceId}`
      const rowKey = `${renameScope}:${notebookRowKey(notebook)}`
      const renameId = (latestRenameRef.current.get(rowKey) ?? 0) + 1
      latestRenameRef.current.set(rowKey, renameId)
      const isLatestRename = () =>
        latestRenameRef.current.get(rowKey) === renameId
      if (notebook.kind === "generated") {
        const renamed = await renameGeneratedNotebook(
          organizationSlug,
          workspaceId,
          notebook.id,
          title,
        )
        if (!isLatestRename()) {
          return
        }
        setGenerated((prev) =>
          prev?.scope === renameScope
            ? {
                ...prev,
                catalog: {
                  ...prev.catalog,
                  notebooks: prev.catalog.notebooks.map((item) =>
                    // A catalog read newer than the rename keeps its own title.
                    item.id === notebook.id &&
                    (item.revision ?? 0) <= (renamed.revision ?? 0)
                      ? {
                          ...item,
                          title: renamed.title,
                          revision: renamed.revision,
                        }
                      : item,
                  ),
                },
              }
            : prev,
        )
        return
      }
      const renamed = await renameNotebook(
        organizationSlug,
        workspaceId,
        notebook.id,
        title,
      )
      if (currentScopeRef.current !== renameScope || !isLatestRename()) {
        return
      }
      setChats((prev) =>
        prev
          ? prev.map((item) =>
              item.id === notebook.id
                ? { ...item, title: renamed.title }
                : item,
            )
          : prev,
      )
    },
    [organizationSlug, workspaceId],
  )

  const renderNameEditor = useCallback(
    (notebook: NotebookRow) =>
      renamingRow &&
      notebookRowKey(renamingRow) === notebookRowKey(notebook) ? (
        <RowNameEditor
          label="Notebook name"
          resource="notebook"
          value={notebook.title}
          maxLength={NOTEBOOK_TITLE_MAX_LENGTH}
          isNameTaken={(name) =>
            (notebooks ?? []).some(
              (other) =>
                notebookRowKey(other) !== notebookRowKey(notebook) &&
                isSameResourceName(other.title, name),
            )
          }
          onSave={(title) => rename(notebook, title)}
          onClose={() => setRenaming(null)}
        />
      ) : null,
    [notebooks, rename, renamingRow],
  )
  const columns = useMemo(
    () => buildNotebookColumns({ renderNameEditor, resolveCreator }),
    [renderNameEditor, resolveCreator],
  )

  const rowActions = useCallback(
    (row: NotebookRow): RowAction<NotebookRow>[] =>
      row.kind === "series"
        ? [{ label: "View editions", icon: <Eye />, onSelect: open }]
        : [
            {
              label: "Open",
              icon: <Eye />,
              onSelect: open,
            },
            {
              label: "Rename",
              icon: <Pencil />,
              skipFocusReturn: true,
              onSelect: (notebook) => setRenaming({ row: notebook, scope }),
            },
            {
              label: "Copy Link",
              icon: <Link2 />,
              onSelect: (notebook) => {
                void navigator.clipboard.writeText(
                  new URL(
                    notebook.kind === "generated"
                      ? generatedHref(notebook.id)
                      : hrefFor(notebook.id),
                    window.location.origin,
                  ).toString(),
                )
              },
            },
            // Sofia has no delete for generated notebooks yet.
            ...(row.kind === "chat"
              ? [
                  {
                    label: "Delete",
                    icon: <Trash2 />,
                    destructive: true,
                    onSelect: (notebook: NotebookRow) =>
                      setDeleting({ row: notebook, scope }),
                  },
                ]
              : []),
          ],
    [generatedHref, hrefFor, open, scope],
  )

  const tableControls = useMemo<ToolbarItem[]>(
    () => [
      {
        type: "search",
        id: "search",
        inputRef: searchInputRef,
        search: {
          placeholder: "Search by title or ID…",
          value: searchValue,
          onChange: (event) => setSearchValue(event.target.value),
          onClear: () => setSearchValue(""),
          "aria-label": "Search notebooks",
          "data-testid": "notebooks-search-input",
        },
      },
    ],
    [searchValue],
  )
  const collectionFilters = useCollectionFilters(notebooks ?? [], [
    {
      id: "created",
      label: "Created",
      kind: "date",
      value: (row) => row.created_at,
    },
    {
      id: "creator",
      label: "Created By",
      kind: "select",
      options: peopleOptions ?? [],
      value: (row) => row.created_by,
      formatValue: resolveCreator,
    },
  ])
  const filtered = useMemo(() => {
    const needle = searchValue.trim().toLowerCase()
    return needle
      ? collectionFilters.rows.filter(
          (notebook) =>
            notebook.title.toLowerCase().includes(needle) ||
            notebook.id.toLowerCase().includes(needle) ||
            notebook.editions?.some(
              (edition) =>
                edition.title.toLowerCase().includes(needle) ||
                edition.id.toLowerCase().includes(needle),
            ) ||
            notebook.template?.id.toLowerCase().includes(needle),
        )
      : collectionFilters.rows
  }, [collectionFilters.rows, searchValue])

  const seriesCount =
    notebooks?.filter((row) => row.kind === "series").length ?? 0
  const selectedSeries =
    series?.scope === scope
      ? (notebooks?.find(
          (row) => row.kind === "series" && row.id === series.id,
        ) ?? null)
      : null
  const isLoading = notebooks === null
  const hasActiveFilters =
    Boolean(searchValue.trim()) || collectionFilters.controls.activeCount > 0

  return (
    <TableDisplayScope
      search={searchValue}
      filterControls={collectionFilters.controls}
      className={cn("flex h-full w-full flex-col", TABLE_CARD_CLASS)}
      data-testid="notebooks-page"
    >
      <div className="table-pad-x flex flex-wrap items-start justify-between gap-4 pt-5">
        <div>
          <div className="flex items-baseline gap-3">
            <h2 className="text-lg font-semibold text-coval-text">Notebooks</h2>
            {notebooks ? (
              <span className="text-sm text-coval-text-muted">
                {seriesCount > 0
                  ? `${seriesCount} series · ${notebooks.length - seriesCount} one-off`
                  : `${notebooks.length} ${notebooks.length === 1 ? "notebook" : "notebooks"}`}
              </span>
            ) : null}
          </div>
          <p className="text-sm text-coval-text-muted">
            Scheduled series and one-off notebooks. Open a series to see its
            editions.
          </p>
        </div>
        {reportsEnabled && (
          <div className="flex items-center gap-4">
            <Link
              href={queryParams.getHref(
                `${NOTEBOOK_BUILDER_PATH}?recipe=1`,
                organizationSlug,
                workspaceId,
              )}
              className="text-sm text-coval-info-text underline"
            >
              From recipe or schedule
            </Link>
            <NewResourceButton
              resourceName="Notebook"
              href={queryParams.getHref(
                NOTEBOOK_BUILDER_PATH,
                organizationSlug,
                workspaceId,
              )}
              data-testid="notebooks-create-link"
            />
          </div>
        )}
      </div>
      <TableControlsRow>
        <TableBrowseToolbar
          items={tableControls}
          filter={<CollectionFilter controls={collectionFilters.controls} />}
        />
      </TableControlsRow>
      <NotebookSeriesDialog
        row={selectedSeries}
        onClose={() => setSeries(null)}
        hrefFor={
          selectedSeries?.seriesStorage === "chat" ? hrefFor : generatedHref
        }
      />
      <DeleteNotebookDialog
        open={deletingRow !== null}
        title={deletingRow?.title ?? ""}
        pending={deletePending}
        onClose={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
      />
      {error ? (
        <div className="table-pad shrink-0 text-sm text-coval-danger-text">
          {error}
        </div>
      ) : null}
      {generatedErrorMessage ? (
        <div
          role="alert"
          className="table-pad flex shrink-0 items-center gap-2 text-sm text-coval-danger-text"
        >
          {generatedErrorMessage}
          <Button
            variant="ghost"
            onClick={() => {
              setGeneratedError(null)
              setCatalogSettledScope(null)
              setGeneratedAttempt((attempt) => attempt + 1)
            }}
          >
            Retry
          </Button>
        </div>
      ) : null}
      {!isLoading && notebooks.length === 0 && !hasActiveFilters ? (
        <EmptyStateCTA
          title="No notebooks yet"
          description={
            reportsEnabled
              ? "Create a notebook, or save a Sofia chat as one."
              : "Save a Sofia chat to create a notebook."
          }
        />
      ) : (
        <Table
          groupOptions={[
            {
              id: "creator",
              label: "Created by",
              value: (row) => row.created_by,
              format: resolveCreator,
            },
          ]}
          emptyState={
            <EmptyFilteredState
              title="No notebooks found"
              resetLabel={
                collectionFilters.controls.activeCount > 0
                  ? "Clear filters"
                  : "Clear search"
              }
              onReset={() => {
                setSearchValue("")
                collectionFilters.controls.reset()
              }}
            />
          }
          aria-label="Notebooks table"
          data={filtered}
          columns={columns}
          getRowId={notebookRowKey}
          onRowClick={open}
          rowActions={rowActions}
          isLoading={isLoading}
          className="h-full"
        />
      )}
    </TableDisplayScope>
  )
}
